import React, { useEffect, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useThemeMode } from '../../ThemeContext';
import { APP_URLS } from '../../helpers/url_helper';
import './StripePayment.css';

// Import assets
import checkmarkIcon from '../../assets/checkmark.svg';

function PaymentSuccess() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [sessionData, setSessionData] = useState(null);
  const [paymentProcessed, setPaymentProcessed] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initialize theme on component mount
  useEffect(() => {
    document.body.classList.remove('dark-mode', 'light-mode');
    document.body.classList.add(mode === 'dark' ? 'dark-mode' : 'light-mode');
  }, [mode]);

  // Get session details from URL params and process payment success
  useEffect(() => {
    const processPaymentSuccess = async () => {
      let sessionId = searchParams.get('session_id');
      const planName = searchParams.get('plan');
      const billingCycle = searchParams.get('billing');
      
      console.log('=== STRIPE PAYMENT SUCCESS - INITIAL DATA ===');
      console.log('Session ID from URL:', sessionId);
      console.log('Plan from URL:', planName);
      console.log('Billing Cycle from URL:', billingCycle);
      console.log('All URL Params:', Object.fromEntries(searchParams));
      
      // If no session_id in URL, try to get from localStorage
      if (!sessionId) {
        const storedSessionId = localStorage.getItem('pendingStripeSessionId');
        if (storedSessionId) {
          sessionId = storedSessionId;
          console.log('=== USING STORED SESSION ID ===');
          console.log('Retrieved session_id from localStorage:', sessionId);
          // Clear the stored session_id since we're using it now
          localStorage.removeItem('pendingStripeSessionId');
        }
      }

      if (sessionId && !paymentProcessed) {
        try {
          setPaymentProcessed(true);
          
          // Prevent duplicate processing
          const processedKey = `processed_${sessionId}`;
          if (localStorage.getItem(processedKey)) {
            console.log('Payment already processed for this session, skipping...');
            return;
          }
          localStorage.setItem(processedKey, 'true');
          
          // Store session_id immediately as backup
          localStorage.setItem('lastStripeSessionId', sessionId);
          console.log('Session ID stored in localStorage for backup:', sessionId);
          
          // Update session data with basic info
          setSessionData({
            sessionId,
            planName: planName || 'Selected Plan',
            billingCycle: billingCycle || 'monthly'
          });

          // API call commented out - using only existing plan details
          // console.log('=== CALLING PAYMENT SUCCESS API ===');
          // const paymentData = {
          //   session_id: sessionId
          // };
          
          // console.log('Payment data being sent to API:', paymentData);
          
          // const successResponse = await stripeAPI.paymentSuccess(paymentData);
          
          // console.log('=== PAYMENT SUCCESS API RESPONSE ===');
          // console.log('Full Success Response:', successResponse);
          // console.log('Success Response Status:', successResponse.status);
          // console.log('Success Response Data:', successResponse.data);
          // console.log('Success Response Message:', successResponse.message);
          
          // Store the API response data for UI display
          // setPaymentApiResponse(successResponse);
          setLoading(false);
          
          // Store basic data in localStorage for future reference
          const storageData = {
            timestamp: new Date().toISOString(),
            sessionId: sessionId,
            planName: planName,
            billingCycle: billingCycle
          };
          
          localStorage.setItem('completePaymentData', JSON.stringify(storageData));
          
          console.log('=== BASIC PAYMENT DATA STORED ===');
          console.log('Stored Data:', storageData);
          console.log('Data saved to localStorage with key: completePaymentData');
          
        } catch (error) {
          console.error('=== PAYMENT PROCESSING ERROR ===');
          console.error('Error:', error);
          console.error('Error Message:', error.message);
          console.error('Error Stack:', error.stack);
          
          // Store error in localStorage for debugging (including session_id for backup)
          localStorage.setItem('paymentProcessingError', JSON.stringify({
            timestamp: new Date().toISOString(),
            error: error.message,
            stack: error.stack,
            sessionId: sessionId
          }));
          
          // Ensure session_id is stored even if there's an error
          if (sessionId) {
            localStorage.setItem('lastStripeSessionId', sessionId);
          }
          
          // Set basic session data even if error occurs
          setSessionData({
            sessionId,
            planName: planName || 'Selected Plan',
            billingCycle: billingCycle || 'monthly',
            error: error.message
          });
          setLoading(false);
        }
      } else if (!sessionId) {
        console.error('=== NO SESSION ID AVAILABLE ===');
        console.error('No session_id found in URL parameters');
        console.error('No session_id found in localStorage backup');
        console.error('Current URL:', window.location.href);
        console.error('This indicates a problem with session ID storage or Stripe configuration');
        
        // Still show basic success info even without session_id
        setSessionData({
          planName: planName || 'Selected Plan',
          billingCycle: billingCycle || 'monthly',
          error: 'Payment completed but session tracking failed'
        });
        setLoading(false);
        
        // Don't call API without session_id as it's required
        console.warn('Skipping payment success API call - session_id is required');
      }
    };

    processPaymentSuccess();
  }, [searchParams, paymentProcessed]);

  const handleGoToDashboard = () => {
    navigate(APP_URLS.DASHBOARD);
  };

  const handleGoToSubscription = () => {
    navigate(APP_URLS.SUBSCRIPTION);
  };

  return (
    <div className="payment-result-page">
      <div className="payment-success-centered-layout">
        {/* Centered Header */}
        <div className="success-header-centered">
          <h1 className="payment-title success-title">
            <img src={checkmarkIcon} alt="Success" className="success-main-icon" />
            Payment Successful!
          </h1>
          <p className="payment-message">
            Thank you for your subscription! Your payment has been processed successfully.
          </p>
        </div>

        {/* Two Boxes Side by Side */}
        <div className="success-boxes-container">
          {/* What's Next Box */}
          <div className="success-box next-steps-box">
            <h5 className="next-steps-title">What's Next?</h5>
            <ul className="next-steps-list">
              <li>Access all premium features immediately</li>
              <li>Check your email for the subscription confirmation</li>
              <li>Start managing your compliance projects</li>
            </ul>
          </div>

          {/* Subscription Summary Box */}
          <div className="success-box subscription-summary-box">
            <h4 className="summary-title">Subscription Summary</h4>
            
            {/* Loading spinner while waiting for data */}
            {loading && (
              <div className="loading-container">
                <Spinner animation="border" role="status" className="loading-spinner">
                  <span className="visually-hidden">Loading subscription details...</span>
                </Spinner>
                <p className="loading-text">Loading your subscription details...</p>
              </div>
            )}

            {/* Error state - only show if there's an error and we're not loading */}
            {!loading && sessionData && sessionData.error && (
              <div className="error-container">
                <p className="error-message">
                  There was an issue retrieving your subscription details, but your payment was successful.
                </p>
              </div>
            )}

            {/* Subscription Details from URL Parameters */}
            {!loading && sessionData && (
              <>
                <div className="summary-item">
                  <span className="summary-label">Plan:</span>
                  <span className="summary-value">{sessionData.planName}</span>
                </div>
                
                <div className="summary-item">
                  <span className="summary-label">Billing Cycle:</span>
                  <span className="summary-value">
                    {sessionData.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                  </span>
                </div>
                
                <div className="summary-item">
                  <span className="summary-label">Status:</span>
                  <span className="summary-value">
                    <span className="auto-renewal-status">Payment Successful</span>
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Centered Action Buttons */}
        <div className="success-actions-centered">
          <Button 
            variant="primary" 
            className="action-btn primary-action"
            onClick={handleGoToDashboard}
          >
            Go to Dashboard
          </Button>
          <Button 
            variant="outline-secondary" 
            className="action-btn secondary-action"
            onClick={handleGoToSubscription}
          >
            View Subscription
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccess;
